<template>
 <section>
      <div class="container cont checkout-step checkout-step--clean">
        <div class="checkout-step__intro">
          <small class="form-text text-muted checkout-step__eyebrow">Paso 1 de 4</small>
          <h2 class="va-h6 checkout-step__title"><strong>Selecciona tus accesos</strong></h2>
          <p class="checkout-step__hint">Elige la cantidad ideal y confirma tu total en una sola vista.</p>
          <div v-if="availableTickets.length > 1 && total_quantity === 0" class="checkout-step__scroll-cue" aria-hidden="true">
            <span>Desliza para ver mas opciones</span>
            <i class="fas fa-arrow-down"></i>
          </div>
        </div>

        <div class="checkout-step__selection-layout">
          <div class="checkout-step__selection-main">
            <div class="checkout-ticket-grid">
              <article v-for="(eventTicket, index) in availableTickets" :key="index" :class="['checkout-ticket-card', { 'is-selected': eventTicket.quantity > 0 }]" :style="{ animationDelay: `${index * 70}ms` }">
                <div :class="['checkout-ticket-card__media', { 'has-image': Boolean(eventTicket.generic_image) }]">
                  <img v-if="eventTicket.generic_image" :src="eventTicket.generic_image" class="checkout-ticket-card__image" alt="">
                  <div v-else class="checkout-ticket-card__placeholder">
                    <i class="fas fa-ticket-alt"></i>
                  </div>
                </div>

                <div class="checkout-ticket-card__body">
                  <div class="checkout-ticket-card__top">
                    <div>
                      <span v-if="eventTicket.quantity > 0" class="checkout-ticket-card__selected">Seleccionado x{{ eventTicket.quantity }}</span>
                      <h3 class="checkout-ticket-card__title">{{ eventTicket.name }}</h3>
                      <p v-if="eventTicket.description" class="checkout-ticket-card__description">{{ eventTicket.description }}</p>
                    </div>
                    <div class="checkout-ticket-card__price">
                      <span v-if="eventTicket.is_courtesy">Cortesía</span>
                       <span v-else>{{ formatMoney(eventTicket.amount) }}</span>
                    </div>
                  </div>

                  <div class="checkout-ticket-card__bottom">
                    <div v-if="dateValidation(eventTicket.expiration_date, eventTicket.sales_date, eventTicket) === 1" class="quantity-toggle checkout-ticket-card__quantity">
                      <button @click="decrement(eventTicket.quantity, index)" @mousedown="buttonAnimation($event, 'decrement')" @mouseup="resetAnimation($event)">&minus;</button>
                      <input type="text" :value="eventTicket.quantity" readonly>
                      <button @click="increment(eventTicket.quantity, index)" @mousedown="buttonAnimation($event, 'increment')" @mouseup="resetAnimation($event)">&plus;</button>
                    </div>

                    <div v-if="dateValidation(eventTicket.expiration_date, eventTicket.sales_date, eventTicket) === 2" class="checkout-step__status--soon">Próximo precio</div>
                    <div v-if="dateValidation(eventTicket.expiration_date, eventTicket.sales_date, eventTicket) === 0" class="checkout-step__status--soldout">Agotado</div>

                    <div v-if="shouldShowLowAvailability(eventTicket)" class="checkout-ticket-card__availability checkout-ticket-card__availability--low">Últimos {{ lowAvailabilityCount(eventTicket) }}</div>

                    <div class="checkout-ticket-card__total">Total {{ formatMoney(eventTicket.amount * eventTicket.quantity) }}</div>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <aside :class="['checkout-step__selection-summary', { 'is-empty': total_quantity === 0 }]" aria-live="polite">
            <span class="checkout-step__selection-kicker">{{ summaryKicker }}</span>

            <div class="checkout-step__selection-head">
              <div class="checkout-step__selection-copy">
                <strong>{{ summaryHeadline }}</strong>
                <p>{{ summaryCopy }}</p>
              </div>
              <span class="checkout-step__selection-total">{{ formattedSubtotal }}</span>
            </div>

            <div class="checkout-step__selection-meta">
              <span>{{ total_quantity || 0 }} acceso{{ total_quantity === 1 ? '' : 's' }}</span>
              <span>{{ selectedTypeCount || 0 }} tipo{{ selectedTypeCount === 1 ? '' : 's' }}</span>
              <span>{{ summaryMeta }}</span>
            </div>

            <div v-if="selectedTickets.length" class="checkout-step__selection-list">
              <article v-for="ticket in selectedTickets" :key="ticket.id" class="checkout-step__selection-item">
                <div>
                  <strong>{{ ticket.name }}</strong>
                  <small>x{{ ticket.quantity }}</small>
                </div>
                <span>{{ formatMoney(ticket.total) }}</span>
              </article>
            </div>

            <div v-else class="checkout-step__selection-empty">
              Tu resumen aparecerá aquí con accesos, cantidades y subtotal apenas agregues la primera selección.
            </div>

            <button @click="continueSale()" :disabled="total_quantity===0" class="btn btn-primary checkout-step__button checkout-step__button--summary liquid-page-button" type="button">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">{{ summaryCta }}</span>
                <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
              </span>
            </button>
          </aside>
        </div>

        <Teleport to="body">
          <div class="checkout-step__mobile-dock" aria-live="polite">
            <div :class="['checkout-step__mobile-card', { 'is-empty': total_quantity === 0 }]">
              <div class="checkout-step__mobile-copy">
                <span class="checkout-step__mobile-total">{{ formattedSubtotal }}</span>
                <strong>{{ mobileHeadline }}</strong>
                <small>{{ mobileSummaryLine }}</small>
              </div>

              <button @click="continueSale()" :disabled="total_quantity===0" class="btn btn-primary checkout-step__button checkout-step__button--mobile liquid-page-button" type="button">
                <span class="liquid-page-button__glow"></span>
                <span class="liquid-page-button__ring"></span>
                <span class="liquid-page-button__inner">
                  <span class="liquid-page-button__copy">{{ mobileCta }}</span>
                  <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
                </span>
              </button>
            </div>
          </div>
        </Teleport>

      </div>
  </section>
</template>


<script>
//import { apiService } from "../../common/api.services.js";
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'

export default {
  
    name: 'SaleQuantity',
    
    props:{
        eventTickets: {
          type: Array,
          default: () => [],
       },
       coordinated_fee:{
         required: true
       },

       fixed_fee:{
         required: true
        },

        paymentOptions: {
          type: Array,
          default: () => [],
        },

        presentmentCurrency: {
          type: String,
          default: 'USD',
        },


       coordinated_service:{
         required: true
        }
     },

     

     data(){
     return{
       events:[],
       next:null,
       availableTickets:[],
       order:[],
       total: 0.00,
       subtotal: 0.00,
       fee: 0.00,
       total_quantity: 0,
       today: new Date(),
       hasMap: 0
       
     }
     

    },

    computed: {
      selectedTypeCount() {
        return this.availableTickets.filter((ticket) => ticket.quantity > 0).length
      },

      selectedTickets() {
        return this.availableTickets
          .filter((ticket) => ticket.quantity > 0)
          .map((ticket) => ({
            id: ticket.id,
            name: ticket.name,
            quantity: ticket.quantity,
            total: (Number.parseFloat(ticket.total) || 0).toFixed(2),
          }))
      },

      formattedSubtotal() {
        const subtotal = Number.parseFloat(this.subtotal) || 0
        return this.formatMoney(subtotal)
      },

      summaryKicker() {
        return 'Resumen'
      },

      summaryHeadline() {
        if (this.total_quantity === 0) {
          return 'Sin accesos seleccionados'
        }

        return `${this.total_quantity} acceso${this.total_quantity === 1 ? '' : 's'} seleccionado${this.total_quantity === 1 ? '' : 's'}`
      },

      mobileHeadline() {
        if (this.total_quantity === 0) {
          return 'Elige tus accesos'
        }

        return `${this.total_quantity} acceso${this.total_quantity === 1 ? '' : 's'} listo${this.total_quantity === 1 ? '' : 's'}`
      },

      summaryCopy() {
        if (this.total_quantity === 0) {
          return 'Agrega al menos un acceso para ver el subtotal y habilitar el siguiente paso.'
        }

        return 'Revisa tu selección, el subtotal y los tipos elegidos antes de continuar a datos.'
      },

      summaryMeta() {
        if (this.total_quantity === 0) {
          return 'Siguiente paso bloqueado'
        }

        return 'Siguiente: datos del comprador'
      },

      mobileSummaryLine() {
        if (this.total_quantity === 0) {
          return 'Tu subtotal aparecerá aquí cuando agregues un acceso.'
        }

        return this.selectedTickets
          .slice(0, 2)
          .map((ticket) => `${ticket.name} x${ticket.quantity}`)
          .join(' · ')
      },

      summaryCta() {
        return this.total_quantity === 0 ? 'Elige tu primer acceso' : 'Continuar con datos'
      },

      mobileCta() {
        return this.total_quantity === 0 ? 'Elegir acceso' : 'Continuar'
      },
    },

     created () {
     this.createTable()
    this.today = new Date();
  },

  
   methods:{
    normalizeCurrency(value) {
      return normalizeCurrencyCode(value || this.presentmentCurrency || 'USD', 'USD')
    },

    formatMoney(value) {
      return formatCurrencyAmount(value, this.normalizeCurrency(this.presentmentCurrency))
    },

    buttonAnimation(event) {
                event.target.style.transform = 'scale(0.9)';
            },
            resetAnimation(event) {
                event.target.style.transform = 'scale(1)';
            },
      lowAvailabilityCount(ticket) {
        const counts = [ticket.remaining_tickets, ticket.host_remaining_tickets]
          .map((value) => Number.parseInt(value, 10))
          .filter((value) => Number.isFinite(value) && value >= 0)

        if (!counts.length) {
          return 0
        }

        return Math.min(...counts)
      },

      shouldShowLowAvailability(ticket) {
        if (this.dateValidation(ticket.expiration_date, ticket.sales_date, ticket) !== 1) {
          return false
        }

        const remaining = this.lowAvailabilityCount(ticket)
        return remaining > 0 && remaining < 10
      },

      createTable() {


    for (var i = 0; i < this.eventTickets.length; i++){
      console.log(i)

      if (this.eventTickets[i].status==1){
      let uProduct =  {remaining_tickets: this.eventTickets[i].remaining_tickets, host_remaining_tickets: this.eventTickets[i].host_remaining_tickets,   quantity:0, amount: this.eventTickets[i].price,  name: this.eventTickets[i].name, is_courtesy:  this.eventTickets[i].is_courtesy, total: 0, id: this.eventTickets[i].id, sales_date: this.eventTickets[i].sales_date,  expiration_date: this.eventTickets[i].expiration_date, quantity_factor: this.eventTickets[i].quantity, map: this.eventTickets[i].has_map, max_quantity: this.eventTickets[i].max_quantity, description: this.eventTickets[i].description, generic_image: this.eventTickets[i].generic_image, guest_fields: Array.isArray(this.eventTickets[i].guest_fields) ? this.eventTickets[i].guest_fields : []};
      this.availableTickets=this.availableTickets.concat(uProduct)
      

    }
        }

      
          
      },

  activePricingProfile() {
    if (!Array.isArray(this.paymentOptions) || this.paymentOptions.length === 0) {
      return null
    }

    const option = this.paymentOptions[0]
    if (!option || !option.pricing_profile) {
      return null
    }

    return option.pricing_profile
  },

  continueSale(){
    if(this.subtotal!=0){
        //this.fee=(this.subtotal * 0.083333) + 0.5
        const pricingProfile = this.activePricingProfile()
        let coordinated_fee=parseFloat(pricingProfile?.platform_fee_percent ?? this.coordinated_fee)
        let coordinated_service=parseFloat(pricingProfile?.platform_fee_per_ticket ?? this.coordinated_service)
        let fixed_fee=parseFloat(pricingProfile?.platform_fee_fixed ?? this.fixed_fee)

        this.fee=(this.subtotal *coordinated_fee) + (coordinated_service * this.total_quantity) + fixed_fee
      
    }else{
      this.fee=0
    }
      this.total=this.subtotal+this.fee
      
      this.subtotal=this.subtotal.toFixed(2)
      this.fee=this.fee.toFixed(2)
      this.total=this.total.toFixed(2)


      this.$emit('messageFromCart', this.availableTickets, this.subtotal, this.fee, this.total, this.hasMap)
    },


    dateValidation(str, sales, obj) {
    let exp = null;
    let saleDate = null;

    // Asignación inicial de fechas
    if (str) exp = new Date(str);
    if (sales) saleDate = new Date(sales);

    // Validación de fechas desde el objeto
    let datestr = exp; // Fecha de expiración
    let dateexp = saleDate; // Fecha de ventas

    if (obj.venue_expiration_date !== undefined && obj.venue_expiration_date !== null) {
        const venueExpDate = new Date(obj.venue_expiration_date);
        if (!datestr || venueExpDate < datestr) {
            datestr = venueExpDate; // Usa la fecha de expiración más próxima
            console.log("Actualizando fecha de expiración");
        }
    }

    if (obj.venue_sales_date !== undefined && obj.venue_sales_date !== null) {
        const venueSalesDate = new Date(obj.venue_sales_date);
        if (!dateexp || venueSalesDate < dateexp) {
            dateexp = venueSalesDate; // Usa la fecha de venta más próxima
            console.log("Actualizando fecha de ventas");
        }
    }

    exp = datestr;
    saleDate = dateexp;

    let var1 = 1; // Por defecto está disponible

    // 0: Agotado
    // 1: Disponible
    // 2: Próximamente

    // Validación de la fecha de expiración
    if (exp !== undefined && exp !== null) {
        const currentDate = new Date();
        if (currentDate >= exp) {
            var1 = 0; // Expirado
            console.log("Estado: Agotado por expiración");
        }
    }

    // Validación de boletos restantes
    if (obj.remaining_tickets <= 0 || obj.host_remaining_tickets <= 0) {
        var1 = 0; // Agotado
        console.log("Estado: Agotado por boletos restantes");
    }

    // Validación de la fecha de ventas
    if (saleDate !== undefined && saleDate !== null) {
        const currentDate = new Date();
        if (currentDate < saleDate) {
            var1 = 2; // Próximamente disponible
            console.log("Estado: Próximamente disponible");
        }
    }

    console.log("Estado final:", var1);
    return var1; // Devuelve el estado final
},



    


    openEvent(_id) {
      this.$router.push({ name: "event", params: { id: _id } });
    },


    increment (quantity, i) {

      let max=this.availableTickets[i].max_quantity

      if (this.availableTickets[i].remaining_tickets<max){
        max=this.availableTickets[i].remaining_tickets
      }



      if(quantity === max ) {

        this.availableTickets[i].quantity==this.availableTickets[i].max_quantity
      
      
      }else{

      if (this.availableTickets[i].quantity!=this.availableTickets[i].max_quantity){
      this.availableTickets[i].quantity++
      this.availableTickets[i].total= (this.availableTickets[i].quantity * this.availableTickets[i].amount).toFixed(2)
       this.availableTickets[i].amount=this.availableTickets[i].amount*1
      this.subtotal=this.subtotal + this.availableTickets[i].amount
      this.subtotal.toFixed(2)
      this.total_quantity=this.total_quantity+(this.availableTickets[i].quantity_factor)
      
      if (this.availableTickets[i].map==true){
        this.hasMap++
      }

    }



           }
    },
    decrement (quantity, i) {
      if(quantity === 0) {
         this.availableTickets[i].quantity=0
           }else{

     
        this.availableTickets[i].quantity--
      this.availableTickets[i].total= (this.availableTickets[i].quantity * this.availableTickets[i].amount).toFixed(2)
       this.availableTickets[i].amount=this.availableTickets[i].amount*1
      this.subtotal=this.subtotal - this.availableTickets[i].amount
      this.subtotal.toFixed(2)
      this.total_quantity=this.total_quantity-(this.availableTickets[i].quantity_factor)

      if (this.availableTickets[i].map==true){
        this.hasMap--
      }

       }
    
      
    }

    
   }
}
</script>

<style>

#imagemPosterSlide{
  padding: 10px;
  
}

.container{
  justify-content:center
}


  
</style>



<style lang="css">
h1 {
  text-align: center;
  margin-bottom: 50px;
  margin-top: 50px;
}

.blog-card-blog {
  margin-top: 30px;
}

.blog-card {
  display: inline-block;
  position: relative;
  width: 100%;
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.87);
  background: rgb(52, 58, 64);
  background-color: rgb(52, 58, 64);
}



.blog-card .blog-table {
  padding: 15px 30px;
}

.blog-table {
  margin-bottom: 0px;
}

.blog-category {
  position: relative;
  line-height: 0;
  margin: 15px 0;
}

.blog-text-success {
  color: rgb(198,197,197);
}

.blog-card-blog .blog-card-caption {
  margin-top: 5px;
}

.blog-card-caption {
  font-weight: 700;
  font-family: "Roboto Slab", "Times New Roman", serif;
}

.blog-card-caption, .blog-card-caption a {
  color: #333;
  text-decoration: none;
}

p {
  color: #3C4857;
  margin-top: 0;
  margin-bottom: 1rem;
}

.blog-card .ftr {
  margin-top: 15px;
}

.blog-card .ftr .author {
  color: #888;
}

.blog-card .ftr div {
  display: inline-block;
}

.blog-card .author .avatar {
  width: 36px;
  height: 36px;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 5px;
}

.blog-card .ftr .stats {
  position: relative;
  top: 1px;
  font-size: 14px;
}

.blog-card .ftr .stats {
  float: right;
  line-height: 30px;
}

a {
  text-decoration: none;
}




  .quantity-toggle input {
    width: 2.5rem;
    text-align: center;
    padding: 0 .5rem;
    background: transparent;
  }
  
  .quantity-toggle button {
    background: transparent;
    color: white;
    font-size: 1rem;
    cursor: pointer;
    border: none;
    
  }


.cont{
   padding-right: 5px;
   padding-left: 5px;
}

.checkout-ticket-card__availability--low {
  background:
    radial-gradient(circle at 14% 0%, rgba(255, 255, 255, 0.14), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.03)),
    rgba(10, 11, 16, 0.82) !important;
  border: 1px solid rgba(214, 206, 236, 0.22) !important;
  color: #eef3ff !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 10px 24px rgba(0, 0, 0, 0.16),
    0 0 0 1px rgba(178, 165, 220, 0.06);
}

.checkout-ticket-card__availability--low::before {
  background: linear-gradient(180deg, #f5f6fb, #9d88de) !important;
  box-shadow: 0 0 12px rgba(157, 136, 222, 0.34) !important;
}


@media (max-width: 768px) {
.cont {
    padding-left: 1px;
    padding-right: 1px;
}

.rw{
   padding-right: 10px;
   padding-left: 10px;


}
}

    
</style>

   
